import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  Checkbox,
  IconButton,
  LinearProgress,
  TableCell,
  TableRow,
  Menu,
  MenuItem,
} from '@mui/material'
import { Visibility as ViewIcon, Delete as DeleteIcon, MoreVert as MoreVertIcon } from '@mui/icons-material'
import { ROUTES } from '@/utils/constants'
import type { Card } from '@/types'

const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
}
function formatCardDate(dateStr: string | undefined): string {
  if (!dateStr) return '—'
  const d = new Date(dateStr)
  return d.toLocaleDateString('ru-RU', DATE_FORMAT_OPTIONS)
}

function getProgressColor(percent: number): 'error' | 'warning' | 'success' {
  if (percent <= 30) return 'error'
  if (percent <= 70) return 'warning'
  return 'success'
}

function getWordText(card: Card): string {
  return card.word?.text ?? ''
}

function getWordTranscription(card: Card): string {
  const attrs = card.word?.attributes
  if (!attrs) return ''
  const a = attrs.find((x) => x.key === 'transcription')
  return a?.value ?? ''
}

function getWordTranslation(card: Card): string {
  const attrs = card.word?.attributes
  if (!attrs) return ''
  const a = attrs.find((x) => x.key === 'translation')
  return a?.value ?? ''
}

interface CardItemProps {
  card: Card
  onDelete?: (card: Card) => void
  showActions?: boolean
  userId?: number
  orderBy?: 'created_at' | 'updated_at' | 'progress'
  /** Показывать колонку выбора для массовых операций (учительский вид). */
  selectable?: boolean
  selected?: boolean
  onToggleSelected?: (cardId: number) => void
}

export function CardItem({
  card,
  onDelete,
  showActions = true,
  userId,
  orderBy = 'created_at',
  selectable = false,
  selected = false,
  onToggleSelected,
}: CardItemProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const progress = Math.round(card.progress ?? 0)
  const color = getProgressColor(progress)
  const viewUrl = userId != null ? ROUTES.USER_CARD_VIEW(userId, card.id) : ROUTES.CARDS_VIEW(card.id)

  const handleView = () => {
    setMenuAnchor(null)
    navigate(viewUrl)
  }
  const handleDelete = () => {
    setMenuAnchor(null)
    onDelete?.(card)
  }

  const displayDate = orderBy === 'updated_at' ? card.updated_at : card.created_at

  return (
    <TableRow hover selected={selected}>
      {selectable && (
        <TableCell padding="checkbox">
          <Checkbox
            size="small"
            checked={selected}
            onChange={() => onToggleSelected?.(card.id)}
          />
        </TableCell>
      )}
      <TableCell>
        <Link
          to={viewUrl}
          style={{ textDecoration: 'none', color: 'inherit', fontWeight: 500 }}
        >
          {getWordText(card)}
        </Link>
      </TableCell>
      <TableCell>{getWordTranscription(card) || '—'}</TableCell>
      <TableCell>{getWordTranslation(card) || '—'}</TableCell>
      <TableCell sx={{ minWidth: 120 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, Math.max(0, progress))}
            color={color}
            sx={{ flex: 1, height: 8, borderRadius: 1 }}
          />
          <Typography variant="caption">{progress}%</Typography>
        </Box>
      </TableCell>
      <TableCell>{card.clues_count ?? card.clues?.length ?? 0}</TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {formatCardDate(displayDate)}
        </Typography>
      </TableCell>
      {showActions && (
        <TableCell align="right">
          <IconButton
            size="small"
            aria-label={t('common.actions')}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={handleView}>
              <ViewIcon sx={{ mr: 1 }} fontSize="small" />
              {t('cards.view')}
            </MenuItem>
            {onDelete && (
              <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
                <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
                {t('cards.delete')}
              </MenuItem>
            )}
          </Menu>
        </TableCell>
      )}
    </TableRow>
  )
}
